import { Button } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { sortDates } from '@fortress-technology-solutions/fortress-component-library/utils';
import React, { useMemo, useState, useCallback } from 'react';
import DocumentTitle from 'react-document-title';
import moment from 'moment';
import TableLayout from '../../components/TableLayoutV2';
import ConditionalTooltip from '../../components/ConditionalTooltip';
import useIntl from '../../hooks/useIntl';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useHudVoucherPermissions from '../../hooks/useHudVoucherPermissions';
import VoucherSetupModal from './VoucherSetupModal';
import messages from './messages';
import { useManageHudVouchersTable } from './hooks';
import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useBaseTableProps from '../../hooks/useBaseTableProps';
import { useVoucherRumContext } from '../ManageHudVoucherDetails/hooks.useVoucherRumContext';

const ManageHUDVouchers = () => {
  useVoucherRumContext();
  const baseTableProps = useBaseTableProps();
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const propertyName = selectedProperty?.name ?? '';
  const organizationId = selectedProperty?.organizationId;
  const propertyId = selectedProperty?.id;
  const [modalMode, setModalMode] = useState(null);
  const closeModal = useCallback(() => setModalMode(null), []);
  const { canSetup, canManage } = useHudVoucherPermissions();

  const {
    vouchers,
    isLoading,
    isError: loadError,
    allColumnsHidden,
    columnOptions,
    count,
    totalCount,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    handleSettingsClose,
    name,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
  } = useManageHudVouchersTable({
    organizationId,
    propertyId,
    propertyName,
    canManage,
  });

  const allVouchersAreDraft = useMemo(() => {
    if (vouchers.length === 0) return false;
    return vouchers.every((voucher) => voucher.submissionStatus === 'Draft');
  }, [vouchers]);

  const hasVouchers = vouchers.length > 0;

  const initialBeginDate = useMemo(() => {
    if (!allVouchersAreDraft || vouchers.length === 0) return null;

    const sortedVouchers = [...vouchers].sort(
      sortDates('voucherYearMonth', 'ASC'),
    );

    const earliestVoucher = sortedVouchers[0];
    const voucherMonthYear = earliestVoucher?.voucherYearMonth;

    if (!voucherMonthYear) return null;

    return moment(voucherMonthYear, 'YYYY-MM').startOf('month').toISOString();
  }, [vouchers, allVouchersAreDraft]);

  return (
    <DocumentTitle
      title={intl.formatMessage(messages.pageTitle, { propertyName })}
    >
      <TableLayout name={name}>
        {(tableHeight) => (
          <>
            <TableHeader
              title={intl.formatMessage(messages.header)}
              subtitle={intl.formatMessage(messages.description)}
              totalCount={totalCount}
              count={count}
              actions={
                <>
                  {!isLoading && !loadError && allVouchersAreDraft && (
                    <ConditionalTooltip
                      show={!canSetup}
                      title={intl.formatMessage(messages.noPermissionTooltip)}
                      placement="top"
                    >
                      <Button
                        variant="shout"
                        disabled={!canSetup}
                        onClick={() =>
                          setModalMode(VoucherSetupModal.MODES.MANAGE)
                        }
                      >
                        {intl.formatMessage(messages.manageSetup)}
                      </Button>
                    </ConditionalTooltip>
                  )}
                  {!isLoading && !loadError && !hasVouchers && (
                    <ConditionalTooltip
                      show={!canSetup}
                      title={intl.formatMessage(messages.noPermissionTooltip)}
                      placement="top"
                    >
                      <Button
                        variant="shout"
                        disabled={!canSetup}
                        onClick={() =>
                          setModalMode(VoucherSetupModal.MODES.SETUP)
                        }
                      >
                        {intl.formatMessage(messages.setUp)}
                      </Button>
                    </ConditionalTooltip>
                  )}
                </>
              }
            />
            <Table
              {...baseTableProps}
              allColumnsHidden={allColumnsHidden}
              columnOptions={columnOptions}
              count={count}
              dateState={dateState}
              filterState={filterState}
              filterTypeState={filterTypeState}
              handleColumnChange={handleColumnChange}
              handleDateSubmit={handleDateSubmit}
              handleFilterChange={handleFilterChange}
              handleFilterTypeChange={handleFilterTypeChange}
              handleSearchSubmit={handleSearchSubmit}
              handleSortChange={handleSortChange}
              handleSettingsClose={handleSettingsClose}
              headers={filteredHeaders}
              height={tableHeight}
              isLoading={isLoading}
              name={name}
              order={order}
              orderBy={orderBy}
              rows={rows}
              searchState={searchState}
              selectedColumns={selectedColumns}
              totalCount={totalCount}
              emptyStateMessage={intl.formatMessage(messages.emptyStateMessage)}
            />
            <VoucherSetupModal
              open={!!modalMode}
              onClose={closeModal}
              organizationId={organizationId}
              propertyId={propertyId}
              initialBeginDate={initialBeginDate}
              mode={modalMode}
            />
          </>
        )}
      </TableLayout>
    </DocumentTitle>
  );
};

export default ManageHUDVouchers;
